/**
 * /search Blogs panel — load-more + tag-fetches (Phase 4b + 4c)
 *
 * On page load: PARALLEL fetch of
 *   1. /search?q=<term>&type=article&page=1 (Shopify full FTS, HTML)
 *   2. Per-tag fetches for tag-matched blog articles (JSON)
 * Merge + dedupe by URL → REPLACE the server-rendered Blogs grid.
 *
 * Tag-fetches mirror the typing dropdown's blogs pipeline
 * (predictive-search.js: fetchTagMatchedBlogArticles). Same
 * #aro-blog-tags blob, same tagged-recipes section endpoint (returns
 * JSON regardless of blog handle; recipe_data comes back null for
 * non-recipe articles — we just ignore those fields for blog cards).
 *
 * Rationale: Shopify FTS misses articles that only match via a TAG.
 * E.g. "shea" won't find articles tagged "shea butter" unless the
 * word appears in body/title. Tag-fetches catch that class.
 *
 * On "Load more blogs" click: fetch next Shopify page (tag results
 * are all fetched on init — nothing to paginate), extract .blogs-grid
 * <li>s, dedupe, append. Removes button when a page yields no new
 * items or has no next-page link.
 *
 * Filter re-render: theme's renderProductGridContainer replaces
 * #ProductGridContainer.innerHTML. Blogs panel lives inside there → all
 * DOM refs become stale. We hook renderProductGridContainer (same
 * pattern as aro-search-page-filters.js) to re-init after each render.
 */
(function () {
  'use strict';

  var LOADING_LABEL = 'Loading…';
  var BLOG_PATH = '/blogs/natural-organic-skincare-blog';
  var TAG_SECTION = 'predictive-search-tagged-recipes';
  var terms;
  var state; // per-init: { panel, grid, countEl, button, currentPage, seenUrls, loading, id }
  var cachedBlogTags = null;
  // Stale-request guard — see aro-search-page-products.js for rationale.
  var requestId = 0;

  function getTerms() {
    return new URLSearchParams(location.search).get('q') || '';
  }

  function urlKey(a) {
    var href = a && a.getAttribute('href');
    return href ? href.split('?')[0] : null;
  }

  function collectUrls(root) {
    var set = new Set();
    if (!root) return set;
    root.querySelectorAll('a[href^="/blogs/"]').forEach(function (a) {
      var k = urlKey(a);
      if (k) set.add(k);
    });
    return set;
  }

  function fetchPage(page) {
    var url = '/search?q=' + encodeURIComponent(terms) + '&type=article&page=' + page;
    return fetch(url).then(function (r) { return r.text(); }).then(function (html) {
      return new DOMParser().parseFromString(html, 'text/html');
    });
  }

  // ── Phase 4c: tag-matched articles (mirrors predictive-search.js) ──

  // pdp-load-audit 2026-09-22: #aro-blog-tags no longer exists in the page —
  // the tag lists moved out of inline HTML (33.9 KB on EVERY page, read only on
  // demand) into sections/aro-search-tags.liquid. Fetched once per tab session
  // and cached in sessionStorage, shared with the header predictive search and
  // the Recipes tab. Returns a PROMISE; all failure paths resolve to [] exactly
  // as the old `if (!el) return []` guard did.
  function getBlogTagsData() {
    if (cachedBlogTags) return cachedBlogTags;
    var KEY = 'aro-search-tags';
    try {
      var cached = sessionStorage.getItem(KEY);
      if (cached) {
        cachedBlogTags = Promise.resolve(JSON.parse(cached).blogs || []);
        return cachedBlogTags;
      }
    } catch (e) { /* private mode / blocked storage */ }

    cachedBlogTags = fetch('/?section_id=aro-search-tags')
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return [];
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var sec = doc.getElementById('shopify-section-aro-search-tags');
        if (!sec) return [];
        var data;
        try { data = JSON.parse(sec.textContent.trim()); } catch (e) { return []; }
        if (!data || !data.blogs) return [];
        try { sessionStorage.setItem(KEY, JSON.stringify(data)); } catch (e) { /* quota */ }
        return data.blogs;
      })
      .catch(function () { return []; });
    return cachedBlogTags;
  }

  // Now returns a Promise of handles.
  function getMatchingBlogTagHandles(searchTerm) {
    var q = (searchTerm || '').toLowerCase().trim();
    if (!q) return Promise.resolve([]);
    return getBlogTagsData().then(function (tags) {
      return tags
        .filter(function (t) { return (t.name || '').toLowerCase().indexOf(q) !== -1; })
        .map(function (t) { return t.handle; });
    });
  }

  function fetchTagMatchedBlogArticles(searchTerm) {
    return getMatchingBlogTagHandles(searchTerm).then(function (handles) {
    if (handles.length === 0) return Promise.resolve([]);
    var fetches = handles.map(function (h) {
      var url = BLOG_PATH + '/tagged/' + encodeURIComponent(h) + '?section_id=' + TAG_SECTION;
      return fetch(url).then(function (r) {
        return r.ok ? r.text() : '';
      }).then(function (html) {
        if (!html) return [];
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var sec = doc.getElementById('shopify-section-' + TAG_SECTION);
        if (!sec) return [];
        try { return JSON.parse(sec.textContent.trim()) || []; }
        catch (e) { return []; }
      }).catch(function () { return []; });
    });
    return Promise.all(fetches).then(function (arrays) {
      return arrays.reduce(function (acc, arr) { return acc.concat(arr); }, []);
    });
    });
  }

  // Render a tag-matched article (JSON) into a <li> matching the
  // .blog-post structure used by server-rendered / Shopify-search
  // articles. Kept minimal — no excerpt (JSON summary is available
  // but we intentionally keep tag cards tighter to match dropdown
  // parity + avoid re-fetching image dimensions).
  function articleJsonToHtml(article) {
    var url = article.url || '';
    var title = article.title || '';
    var image = article.image || '';
    var escapedTitle = title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    var imgHtml = image
      ? '<img src="' + image + '" alt="' + escapedTitle + '" loading="lazy">'
      : '';
    return (
      '<li class="columns">' +
        '<div class="blog-post aro-search-page__blog-post--tag-matched">' +
          '<figure class="featured-image aspect-ratio" style="--ratio-percent: 100%;">' +
            '<a href="' + url + '" title="' + escapedTitle + '">' + imgHtml + '</a>' +
          '</figure>' +
          '<h5><a href="' + url + '" title="' + escapedTitle + '">' + escapedTitle + '</a></h5>' +
        '</div>' +
      '</li>'
    );
  }

  var PAGE_SIZE = 16;

  // Scan pages 1-MAX_PAGES in batched parallel fetches, dedupe in
  // eagerReplace. Pagination LINKS on /search?type=article are unreliable
  // — Shopify sometimes doesn't render them (no `.pagination a[href*="page="]`
  // in the response), so relying on maxPage detection under-fetches. The
  // observed behaviour for "jojoba" was page 1 → 3 blogs, page 4 → 12
  // blogs (all of them). Blind scan of a fixed page range covers this
  // and matches the dropdown's article count.
  //
  // Cost: MAX_PAGES × ~500ms sequential in worst case, but batched at
  // 3 in parallel is ~4 batches ≈ 2s. Empty pages return quickly.
  // Rate-limit friendly.
  function fetchAllArticleLis() {
    var MAX_PAGES = 10;
    return fetchInBatches(1, MAX_PAGES, 3);
  }

  function fetchInBatches(startPage, endPage, batchSize) {
    var pages = [];
    for (var p = startPage; p <= endPage; p++) pages.push(p);
    var out = [];
    function runBatch(offset) {
      if (offset >= pages.length) return Promise.resolve();
      var batch = pages.slice(offset, offset + batchSize);
      return Promise.all(batch.map(function (page) {
        return fetchPage(page).then(extractLis).catch(function () { return []; });
      })).then(function (results) {
        results.forEach(function (lis) { out = out.concat(lis); });
        return runBatch(offset + batchSize);
      });
    }
    return runBatch(0).then(function () { return out; });
  }

  function extractLis(doc) {
    if (!doc) return [];
    var grid = doc.querySelector('.aro-search-page__blogs-grid');
    if (!grid) return [];
    return Array.prototype.slice.call(grid.children);
  }

  function eagerReplace(myId) {
    if (!state) return;
    Promise.all([
      fetchAllArticleLis().catch(function () { return []; }),
      fetchTagMatchedBlogArticles(terms),
    ]).then(function (results) {
      if (state && state.id !== myId) return; // superseded by newer init
      var shopifyLis = results[0] || [];
      var tagArticles = results[1] || [];

      // Ensure a grid element exists (server may have rendered the empty state).
      if (!state.grid) {
        var empty = state.panel.querySelector('.aro-search-page__panel-empty');
        if (empty) empty.remove();
        var ul = document.createElement('ul');
        ul.className = 'aro-search-page__blogs-grid products collection row small-up-2 medium-up-2 large-up-4';
        // Insert BEFORE pagination-mount so grid renders above pagination.
        var pagMount = state.panel.querySelector('.aro-search-page__pagination-mount');
        if (pagMount) state.panel.insertBefore(ul, pagMount);
        else state.panel.insertBefore(ul, state.panel.firstChild.nextSibling);
        state.grid = ul;
      }

      // Build merged list: tag-matched articles (prepended, higher signal)
      // then Shopify FTS blogs. Dedupe by URL.
      var seen = new Set();
      var mergedLis = [];
      tagArticles.forEach(function (a) {
        if (!a || !a.url) return;
        var key = a.url.split('?')[0];
        if (seen.has(key)) return;
        seen.add(key);
        var tmp = document.createElement('template');
        tmp.innerHTML = articleJsonToHtml(a);
        mergedLis.push(tmp.content.firstElementChild);
      });
      shopifyLis.forEach(function (li) {
        var link = li.querySelector('a[href^="/blogs/"]');
        var key = urlKey(link);
        if (!key || seen.has(key)) return;
        seen.add(key);
        mergedLis.push(li);
      });

      state.allLis = mergedLis;
      state.totalItems = mergedLis.length;

      // Handle empty state.
      if (state.totalItems === 0) {
        state.grid.remove();
        state.grid = null;
        var msg = document.createElement('div');
        msg.className = 'aro-search-page__panel-empty';
        msg.textContent = 'No blog posts found for ‘' + terms + '’';
        state.panel.appendChild(msg);
      } else {
        renderCurrentPage(1);
      }

      // Update count line to show TOTAL (across all client-side pages).
      if (state.countEl) {
        state.countEl.textContent = state.totalItems + ' Blog' +
          (state.totalItems === 1 ? '' : 's') + ' for ‘' + terms + '’';
      }

      state.panel.classList.remove('aro-search-page__panel--loading');
      // Notify sibling panels — see priority tab-load logic below.
      _dispatchActiveTabDoneOnce();
    }).catch(function () {
      if (state && state.id !== myId) return;
      if (state && state.panel) state.panel.classList.remove('aro-search-page__panel--loading');
      // Dispatch even on failure so siblings don't wait forever.
      _dispatchActiveTabDoneOnce();
    });
  }

  function renderCurrentPage(page) {
    if (!state || !state.grid || !state.allLis) return;
    state.currentPage = page;
    var start = (page - 1) * PAGE_SIZE;
    var slice = state.allLis.slice(start, start + PAGE_SIZE);
    state.grid.innerHTML = '';
    slice.forEach(function (li) { state.grid.appendChild(li.cloneNode(true)); });
    buildPagination();
  }

  // Build theme-style pagination structure (matches pagination.liquid so
  // pagination.css styling applies): count + progress bar at top, prev/
  // numbered/next below. Uniform with Products tab.
  function buildPagination() {
    if (!state) return;
    var mount = state.panel.querySelector('.aro-search-page__pagination-mount');
    if (!mount) return;
    var totalPages = Math.ceil(state.totalItems / PAGE_SIZE);
    if (totalPages <= 1) { mount.innerHTML = ''; return; }
    mount.innerHTML = renderThemePagination(state.currentPage, totalPages, state.totalItems, 'blog');
    bindPaginationClicks();
  }

  function bindPaginationClicks() {
    if (!state) return;
    var mount = state.panel.querySelector('.aro-search-page__pagination-mount');
    if (!mount) return;
    mount.querySelectorAll('a[data-page]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        if (a.parentElement.classList.contains('disabled')) return;
        var page = parseInt(a.getAttribute('data-page'), 10);
        if (isNaN(page) || page < 1 || page > Math.ceil(state.totalItems / PAGE_SIZE)) return;
        renderCurrentPage(page);
        state.panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function renderThemePagination(current, totalPages, totalItems, singular) {
    var start = (current - 1) * PAGE_SIZE + 1;
    var end = Math.min(current * PAGE_SIZE, totalItems);
    var progress = totalItems > 0 ? end / totalItems : 0;
    var plural = totalItems === 1 ? singular : singular + 's';
    var prevArrow = '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';
    var nextArrow = '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';
    var pageItems = '';
    for (var p = 1; p <= totalPages; p++) {
      if (p === current) {
        pageItems += '<li class="current"><span class="page">' + p + '</span></li>';
      } else {
        pageItems += '<li class="page"><a href="#" data-page="' + p + '">' + p + '</a></li>';
      }
    }
    return '<div class="pagination--wrapper">' +
      '<div class="thb-filter-count">' +
        '<span class="facets__label">' + start + '-' + end + ' of ' + totalItems + ' ' + plural + '</span>' +
        '<div class="pagination--progress" style="--progress-count: ' + progress + ';"><div class="pagination--progress-bar"></div></div>' +
      '</div>' +
      '<nav class="pagination pagination-type--paginated">' +
        '<div class="row"><div class="small-12 columns">' +
          '<ul class="page-numbers nav-links">' +
            '<li class="prev' + (current === 1 ? ' disabled' : '') + '"><a href="#" data-page="' + (current - 1) + '" aria-label="Previous">' + prevArrow + '</a></li>' +
            pageItems +
            '<li class="next' + (current === totalPages ? ' disabled' : '') + '"><a href="#" data-page="' + (current + 1) + '" aria-label="Next">' + nextArrow + '</a></li>' +
          '</ul>' +
        '</div></div>' +
      '</nav>' +
    '</div>';
  }

  // Removed: injectPagination / bindPaginationClicks / goToPage / loadMore.
  // Superseded by client-side pagination (buildPagination + renderCurrentPage)
  // which operates on the full state.allLis pre-fetched by fetchAllArticleLis.

  function init() {
    var panel = document.querySelector('.aro-search-page__panel--blogs');
    if (!panel) { state = null; return; }
    terms = getTerms();
    if (!terms) { state = null; return; }

    var grid = panel.querySelector('.aro-search-page__blogs-grid');
    var countEl = panel.querySelector('.aro-search-page__blogs-count');

    var myId = ++requestId;
    state = {
      panel: panel,
      grid: grid,
      countEl: countEl,
      button: null,
      currentPage: 1,
      seenUrls: collectUrls(grid),
      loading: false,
      id: myId,
    };

    eagerReplace(myId);
  }

  // Re-init on facet-triggered re-render. Same hook point as
  // aro-search-page-filters.js — the theme's renderProductGridContainer
  // replaces #ProductGridContainer.innerHTML including the Blogs panel.
  function hookRerender() {
    if (!window.FacetFiltersForm) return false;
    if (FacetFiltersForm.__aroBlogsHooked) return true;
    FacetFiltersForm.__aroBlogsHooked = true;
    var orig = FacetFiltersForm.renderProductGridContainer;
    if (typeof orig !== 'function') return true;
    FacetFiltersForm.renderProductGridContainer = function (html) {
      orig.call(this, html);
      // See aro-search-page-products.js for rationale: defer init so
      // updateURLHash (sync-after-forEach in renderPage) runs first —
      // otherwise cached-response paths make us read stale location.search.
      setTimeout(function () { try { init(); } catch (e) { /* silent */ } }, 0);
    };
    return true;
  }

  // Priority tab-load — see aro-search-page-products.js for rationale.
  var _MY_TAB_NAME = 'blogs';
  var _activeTabDispatchedOnce = false;
  function _isActiveTab() {
    var stored = null;
    try { stored = sessionStorage.getItem('aro-search-page-tab'); } catch (e) {}
    return (stored || 'products') === _MY_TAB_NAME;
  }
  function _dispatchActiveTabDoneOnce() {
    if (_activeTabDispatchedOnce) return;
    _activeTabDispatchedOnce = true;
    try { window.dispatchEvent(new Event('aro:search-active-tab-done')); } catch (e) {}
  }

  function bootstrap() {
    if (!hookRerender()) {
      var tries = 0;
      var iv = setInterval(function () {
        tries++;
        if (hookRerender() || tries > 40) clearInterval(iv);
      }, 100);
    }
    if (_isActiveTab()) {
      init();
    } else {
      window.addEventListener('aro:search-active-tab-done', function () {
        init();
      }, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
